import * as React from 'react';

import { parse } from 'qs';
import { FormattedMessage, InjectedIntlProps } from 'react-intl';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';

import { login, loginWithRedirect } from 'actions/account';
import { Button } from 'components/Button';
import { Footer } from 'components/Footer';
import { Form } from 'components/Form';
import { FieldText, InputText } from 'components/Input';
import { LayoutFooter, LayoutTitle } from 'components/Layout';
import { LinkWithAnalytics } from 'components/LinkWithAnalytics';
import { AnalyticsEventAction, AnalyticsEventCategory } from 'constants/analytics';
import { APPLICATION_FORM_NAME, ROUTE_FORGOT_PASSWORD } from 'constants/app';
import { sendEvent } from 'helper/analytics';
import { injectIntl } from 'helper/redux';
import { validate } from 'helper/validation/account/accountLoginForm';
import { AccountLoginForm } from 'models/account';

import './AccountLogin.scss';

interface DispatchProps {
  login: typeof login;
  loginWithRedirect: typeof loginWithRedirect;
}

type OwnProps = InjectedIntlProps & RouteComponentProps<{}>;
type AccountLoginProps = DispatchProps & OwnProps;

@injectIntl()
class AccountLoginContainer extends React.Component<AccountLoginProps, {}> {
  private fieldUsername: FieldText;

  constructor(props: AccountLoginProps) {
    super(props);

    this.isValidSignInRedirectUrl = this.isValidSignInRedirectUrl.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.setFieldUsernameReference = this.setFieldUsernameReference.bind(this);
  }

  componentDidMount() {
    (this.fieldUsername.getRenderedComponent() as InputText).focus();
  }

  render() {
    const { intl } = this.props;
    return (
      <Form
        className="o-container o-container--fluid o-container--fluid-vertical"
        form={APPLICATION_FORM_NAME}
        onSubmit={this.onSubmit}
        validate={validate}
      >
        <LayoutTitle modifier="with-logo" title={intl.formatMessage({ id: 'account.login.title' })}/>
        <LayoutFooter><Footer/></LayoutFooter>

        <FieldText
          className="o-form-field"
          component={InputText}
          label={intl.formatMessage({ id: 'account.login.username' })}
          modifier="cancel"
          name="username"
          ref={this.setFieldUsernameReference}
          type="text"
          withRef={true}
        />
        <FieldText
          name="password"
          label={intl.formatMessage({ id: 'account.login.password' })}
          component={InputText}
          type="password"
          modifier="visibility"
          className="o-form-field"
        />

        <div className="account-login__box">
          <LinkWithAnalytics
            to={ROUTE_FORGOT_PASSWORD}
            className="account-login__link"
            category={AnalyticsEventCategory.ACCOUNT_LOG_IN}
            action={AnalyticsEventAction.OPEN_ACCOUNT_FORGOT_PASSWORD_PAGE}
          >
            <FormattedMessage id="account.login.forgot.password"/>
          </LinkWithAnalytics>
          <Button type="submit" className="account-login__button button--primary">
            <FormattedMessage id="account.login.continue"/>
          </Button>
        </div>
      </Form>
    );
  }

  private onSubmit(formValue: AccountLoginForm) {
    const { location } = this.props;
    const redirectUrl = parse(location.search.replace(/\?/g, '')).redirect_url;

    sendEvent({
      action: AnalyticsEventAction.ACCOUNT_LOG_IN,
      category: AnalyticsEventCategory.ACCOUNT_LOG_IN
    });

    if (redirectUrl && this.isValidSignInRedirectUrl(redirectUrl)) {
      this.props.loginWithRedirect(formValue, redirectUrl);
    } else {
      this.props.login(formValue);
    }
  }

  private isValidSignInRedirectUrl(redirectUrl: string): boolean {
    const signInAllowedRedirectUrls = process.env.REACT_APP_SIGN_IN_ALLOWED_REDIRECT_URLS || '';
    return signInAllowedRedirectUrls
      .split(',')
      .some((allowedUrl) => {
        return allowedUrl === redirectUrl || redirectUrl.startsWith(`${allowedUrl}/`);
      });
  }

  private setFieldUsernameReference(field: FieldText) {
    this.fieldUsername = field;
  }
}

export const AccountLogin = connect<{}, DispatchProps, OwnProps>(
  null, { login, loginWithRedirect }
)(AccountLoginContainer);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Account/Login/AccountLogin/AccountLogin.tsx